import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Zap, Play, Pause } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import AdminLayout from "@/components/layout/AdminLayout";

import { supabase } from "@/integrations/supabase/client";

interface AutomationRule {
  id: number;
  name: string;
  description: string;
  trigger_type: string;
  trigger_conditions: any;
  template_id: number;
  is_active: boolean;
  created_at: string;
  notification_templates?: {
    name: string;
    category: string;
  };
}

const NotificationAutomationList = () => {
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchAutomationRules();
  }, []);

  const fetchAutomationRules = async () => {
    try {
      const { data, error } = await supabase
        .from("notification_automation_rules")
        .select(
          `
          *,
          notification_templates (
            name,
            category
          )
        `
        )
        .order("created_at", { ascending: false });

      if (error) throw error;
      setRules(data || []);
    } catch (error) {
      console.error("Error fetching automation rules:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleRuleStatus = async (ruleId: number, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from("notification_automation_rules")
        .update({ is_active: !currentStatus })
        .eq("id", ruleId);

      if (error) throw error;

      setRules(
        rules.map((rule) =>
          rule.id === ruleId ? { ...rule, is_active: !currentStatus } : rule
        )
      );
    } catch (error) {
      console.error("Error updating rule status:", error);
    }
  };

  const filteredRules = rules.filter(
    (rule) =>
      rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTriggerTypeBadgeColor = (type: string) => {
    const colors = {
      event: "bg-blue-100 text-blue-800",
      schedule: "bg-green-100 text-green-800",
      user_behavior: "bg-purple-100 text-purple-800",
      subscription_change: "bg-orange-100 text-orange-800",
    };
    return colors[type as keyof typeof colors] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <AdminLayout title="Automation Rules">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-muted-foreground">
              Manage automated notification triggers and schedules
            </p>
          </div>
          <Button asChild>
            <Link to="/admin/notifications/automation/new">
              <Plus className="h-4 w-4 mr-2" />
              New Rule
            </Link>
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Automation Rules ({rules.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search automation rules..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rule Name</TableHead>
                  <TableHead>Template</TableHead>
                  <TableHead>Trigger Type</TableHead>
                  <TableHead>Conditions</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{rule.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {rule.description}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {rule.notification_templates?.name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {rule.notification_templates?.category}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={getTriggerTypeBadgeColor(rule.trigger_type)}
                      >
                        {rule.trigger_type.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {rule.trigger_conditions &&
                      typeof rule.trigger_conditions === "object"
                        ? Object.keys(rule.trigger_conditions)
                            .slice(0, 2)
                            .join(", ")
                        : "Custom conditions"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={rule.is_active ? "default" : "secondary"}>
                        {rule.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(rule.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            toggleRuleStatus(rule.id, rule.is_active)
                          }
                        >
                          {rule.is_active ? (
                            <Pause className="h-4 w-4" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <Link
                            to={`/admin/notifications/automation/${rule.id}/edit`}
                          >
                            Edit
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {filteredRules.length === 0 && (
              <div className="text-center py-8">
                <Zap className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-2 text-sm font-semibold">
                  No automation rules found
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {searchTerm
                    ? "Try adjusting your search criteria"
                    : "Get started by creating your first automation rule"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default NotificationAutomationList;
